import { Routes, Router, CanActivateFn, ResolveFn, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { inject } from '@angular/core';
import { IndexedStorageService } from './services/storage.service';
import { ApiService, CREDS_KEY, IAuthCredentials } from './services/api.service';
import { companiesResolver } from './pages/companies/companies-resolver';
import { detallEmpresaResolver } from './pages/evaluation-context/detall-empresa-resolver';
import { ICompanyMeta } from './pages/companies/companies-page';
import { forkJoin, map, switchMap, iif, catchError, of } from 'rxjs';

// obté l'estat de navegació (si hi és)
const navState = ()=>inject(Router).currentNavigation()?.extras.state;

export const loggedInGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const storage = inject(IndexedStorageService);
  const router = inject(Router);
  const api = inject(ApiService);
  return storage.dbGet<IAuthCredentials|undefined>(CREDS_KEY).pipe(
    switchMap(udata=>iif(
      ()=>!!udata,
      api.userInfo().pipe(
        catchError(err=>of(router.parseUrl('/login'))), 
        map(resp=>!!resp)
      ),
      of(router.parseUrl('/login'))
    ))
  );
}

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./pages/login/login-page').then((m) => m.LoginPage),
  },
  {
    path: 'companies',
    canActivate:[loggedInGuard],
    loadComponent: () => import('./pages/companies/companies-page').then((m) => m.CompaniesPage),
    resolve:{
      companies: companiesResolver,
      userInfo: ()=>inject(ApiService).userInfo()
    }
  },
  {
    path: 'evaluation/context',
    canActivate:[loggedInGuard],
    loadComponent: () =>
      import('./pages/evaluation-context/evaluation-context-page').then(
        (m) => m.EvaluationContextPage,
      ),
    resolve:{
      // Li passo la companyia clicada al Navigation state
      company: ()=><ICompanyMeta>navState()!,
      assessments: detallEmpresaResolver,
      // Només serà disponible en retornar de pas posterior (tancament)
      status:()=><{
          assessment_id: number;
      }>navState()!
    }
  },
  {
    path: 'evaluation/element',
    canActivate:[loggedInGuard],
    loadComponent: ()=>import('./pages/evaluation-element/evaluation-element-page').then((m)=>m.EvaluationElementPage),
    resolve:{
      // li passo la info que hem anat acumulant
      status:()=><{
          assessment_id: number;
          sources: { risk_source_type_id: number; risk_source_id: number; risk_name: string;}[];
          company_id: string;
          company_name: string;
          company_groups: { company_group_id: number; company_group_name: string;}[];
      }>navState()!
    }
  },
  {
    path: 'evaluation/capture',
    canActivate:[loggedInGuard],
    loadComponent: ()=>import('./pages/evaluation-capture/evaluation-capture-page').then((m)=>m.EvaluationCapturePage),
    resolve:{
      status:()=><{
        risk: {
            risk_source_type_id: number;
            risk_source_id: number;
            risk_name: string;
        };
        assessment_id: number;
        company_id: string;
        company_name: string;
        company_groups: {
            company_group_id: number;
            company_group_name: string;
        }[];
      }>navState()!
    }
  },
  {
    path: 'evaluation/review',
    loadChildren: ()=>import('./pages/evaluation-review/evaluation-review.routes').then((m) => m.routes),
  },
  {
    path: 'evaluation/closure',
    canActivate:[loggedInGuard],
    loadComponent: () =>
      import('./pages/evaluation-closure/evaluation-closure-page').then(
        (m) => m.EvaluationClosurePage,
      ),
    resolve:{
      status:()=><{
        risk: {
            risk_source_type_id: number;
            risk_source_id: number;
            risk_name: string;
        };
        assessment_id: number;
        company_id: string;
        company_name: string;
        company_groups: {
            company_group_id: number;
            company_group_name: string;
        }[];
        origen:string
      }>navState()!,
      images: ()=>{
        const {assessment_id, risk} = <{
          risk: {risk_source_id: number};
          assessment_id: number;
        }>navState()!
        return inject(IndexedStorageService).getThumbnails(assessment_id, risk.risk_source_id).pipe(
          map(imgs=>imgs.filter(img=>!!img.metadata.data))
        )
      },
      workplaces:()=>{
        const { company_id, assessment_id } = <{company_id:string, assessment_id:number}>navState()!;
        const api = inject(ApiService);
        return forkJoin({
          workplaces:api.workplaces(company_id),
          jobs:api.companyJobs(company_id).pipe(
            switchMap((jobs)=>{
              return forkJoin(jobs.map(
                job=>api.companyJobMachines(company_id, job.company_job_id).pipe(
                  map(machines=>{ return {...job, machines }})
                )
              ));
            })
          ),
          machines:api.assessmentSources(assessment_id, true).pipe(
            map(resp=>resp.filter(r=>r.risk_source_type_id==5).map(r=>{ 
              return {company_machine_id:r.risk_source_id, company_machine_name:r.risk_name}; 
            }))
          )
        });
      },
      relations:()=>{
        const {assessment_id, risk, company_id} = <{
          risk: {risk_source_type_id:number, risk_source_id: number};
          assessment_id: number;
          company_id: string
        }>navState()!
        const api = inject(ApiService);
        return api.riskSourceRelations(assessment_id, company_id, risk.risk_source_type_id, risk.risk_source_id) 
      }
    }
  },
  { path: '', pathMatch: 'full', redirectTo: 'companies' },
  { path: '**', redirectTo: 'companies' },
];
